import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getStats, getSubmissions, getAIUsage, exportSubmissionsCsv } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useAuth } from '../App';

const NOTIFICATION_SEEN_KEY = 'uzjs_admin_notifications_seen_v1';
const NOTIFICATION_BOOTSTRAP_KEY = 'uzjs_admin_notifications_bootstrap_v1';

const notificationMeta = {
  new_submission: {
    label: "Yangi maqola",
    dotClass: 'bg-blue-500',
  },
  revision: {
    label: "Qayta yuborilgan maqola",
    dotClass: 'bg-orange-500',
  },
  payment: {
    label: "To'lov habarnomasi",
    dotClass: 'bg-emerald-500',
  },
};

const toMillis = (dateStr) => {
  if (!dateStr) return 0;
  const value = new Date(dateStr).getTime();
  return Number.isFinite(value) ? value : 0;
};

const buildNotifications = (submissions = []) => {
  const events = [];

  submissions.forEach((sub) => {
    const authorName = sub.author
      ? `${sub.author.last_name || ''} ${sub.author.first_name || ''}`.trim()
      : '';
    const base = {
      submissionId: sub.id,
      issueId: sub.issue_id,
      submissionTitle: sub.title || `Maqola #${sub.id}`,
      authorName,
    };

    const submittedAtMs = toMillis(sub.submitted_at);
    if (submittedAtMs) {
      events.push({
        ...base,
        id: `new:${sub.id}:${sub.submitted_at}`,
        type: 'new_submission',
        happenedAt: sub.submitted_at,
        timestamp: submittedAtMs,
      });
    }

    if (sub.revised_file_path) {
      const revisedAt = sub.updated_at || sub.submitted_at;
      const revisedAtMs = toMillis(revisedAt);
      if (revisedAtMs) {
        events.push({
          ...base,
          id: `revision:${sub.id}:${sub.revised_file_path}:${sub.updated_at || ''}`,
          type: 'revision',
          happenedAt: revisedAt,
          timestamp: revisedAtMs,
        });
      }
    }

    if (sub.payment_receipt_path) {
      const paymentAt = sub.updated_at || sub.submitted_at;
      const paymentAtMs = toMillis(paymentAt);
      if (paymentAtMs) {
        events.push({
          ...base,
          id: `payment:${sub.id}:${sub.payment_receipt_path}:${sub.updated_at || ''}`,
          type: 'payment',
          happenedAt: paymentAt,
          timestamp: paymentAtMs,
        });
      }
    }
  });

  const uniqueEvents = new Map();
  events.forEach((event) => {
    uniqueEvents.set(event.id, event);
  });

  return Array.from(uniqueEvents.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 40);
};

const readSeenNotificationIds = () => {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(NOTIFICATION_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((item) => typeof item === 'string'));
  } catch {
    return new Set();
  }
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { selectedJournalSlug, currentJournal } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [seenNotificationIds, setSeenNotificationIds] = useState(() => readSeenNotificationIds());
  const [aiUsage, setAiUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const notificationRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(NOTIFICATION_SEEN_KEY, JSON.stringify(Array.from(seenNotificationIds)));
    }
  }, [seenNotificationIds]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);

    const loadSubmissionFeed = async ({ silent = false } = {}) => {
      try {
        const subsRes = await getSubmissions({ limit: 200 });
        if (!active) return;
        const submissions = Array.isArray(subsRes.data) ? subsRes.data : [];
        setRecent(submissions.slice(0, 10));

        const events = buildNotifications(submissions);
        setNotifications(events);

        const isBootstrapped =
          typeof window !== 'undefined' &&
          localStorage.getItem(NOTIFICATION_BOOTSTRAP_KEY) === '1';

        if (!isBootstrapped) {
          setSeenNotificationIds(new Set(events.map((event) => event.id)));
          if (typeof window !== 'undefined') {
            localStorage.setItem(NOTIFICATION_BOOTSTRAP_KEY, '1');
          }
        }
      } catch (err) {
        console.error('Submissions fetch error', err);
        if (!silent) {
          toast.error("Maqolalarni yuklashda xatolik");
        }
      }
    };

    const fetchData = async () => {
      try {
        try {
          const statsRes = await getStats();
          if (active) setStats(statsRes.data);
        } catch (err) {
          console.error('Stats fetch error', err);
          toast.error("Statistika yuklanmadi");
        }

        await loadSubmissionFeed();

        try {
          const aiRes = await getAIUsage();
          if (active) setAiUsage(aiRes.data);
        } catch (err) {
          console.warn('AI usage stats unavailable', err?.response?.status || err?.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    const pollId = setInterval(() => {
      loadSubmissionFeed({ silent: true });
    }, 45000);

    return () => {
      active = false;
      clearInterval(pollId);
    };
  }, [selectedJournalSlug]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
    </div>
  );

  const statCards = stats ? [
    {
      label: 'Jami maqolalar', value: stats.total_submissions, gradient: 'stat-gradient-1', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
      )
    },
    {
      label: 'Mualliflar', value: stats.total_authors || 0, gradient: 'stat-gradient-2', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
      )
    },
    {
      label: 'Bu oy', value: stats.this_month, gradient: 'stat-gradient-3', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>
      )
    },
    {
      label: "Kutilmoqda", value: stats.by_status?.submitted || 0, gradient: 'stat-gradient-4', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      )
    },
    {
      label: 'Taqrizda', value: stats.by_status?.under_review || 0, gradient: 'stat-gradient-5', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
      )
    },
    {
      label: 'Qabul qilingan', value: stats.by_status?.accepted || 0, gradient: 'stat-gradient-6', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      )
    },
    {
      label: 'Rad etilgan', value: stats.by_status?.rejected || 0, gradient: 'stat-gradient-7', icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      )
    },
  ] : [];

  const perIssue = stats?.per_issue || [];
  const byAffiliation = stats?.by_affiliation || [];
  const byCountry = stats?.by_country || [];
  const bySection = stats?.by_section || [];
  const sectionByIssue = stats?.section_by_issue || { issues: [], data: {} };

  // dd.mm.yyyy format
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  };

  // Country code to label mapping
  const countryLabels = {
    'UZ': "🇺🇿 O'zbekiston",
    'RU': '🇷🇺 Rossiya',
    'KZ': "🇰🇿 Qozog'iston",
    'KG': "🇰🇬 Qirg'iziston",
    'TJ': '🇹🇯 Tojikiston',
    'TM': '🇹🇲 Turkmaniston',
    'AZ': '🇦🇿 Ozarbayjon',
    'GE': '🇬🇪 Gruziya',
    'US': '🇺🇸 AQSH',
    'GB': '🇬🇧 Britaniya',
    'DE': '🇩🇪 Germaniya',
    'FR': '🇫🇷 Fransiya',
    'TR': '🇹🇷 Turkiya',
    'CN': '🇨🇳 Xitoy',
    'JP': '🇯🇵 Yaponiya',
    'KR': '🇰🇷 Koreya',
    'IN': '🇮🇳 Hindiston',
  };

  const unreadNotificationCount = notifications.reduce((count, item) => (
    seenNotificationIds.has(item.id) ? count : count + 1
  ), 0);

  const markNotificationsAsSeen = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setSeenNotificationIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const markAllNotificationsAsSeen = () => {
    markNotificationsAsSeen(notifications.map((item) => item.id));
  };

  const handleNotificationClick = (item) => {
    markNotificationsAsSeen([item.id]);
    setNotificationOpen(false);
    navigate(`/submissions/${item.submissionId}${item.issueId ? `?issue=${item.issueId}` : ''}`);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportSubmissionsCsv();
      toast.success('CSV yuklab olindi!');
    } catch {
      toast.error('Eksport xatoligi');
    } finally {
      setExporting(false);
    }
  };

  const zeroUsage = {
    total_requests: 0,
    total_input_tokens: 0,
    total_output_tokens: 0,
    total_cost_usd: 0,
    recent: [],
  };

  const reviewUsage = aiUsage?.by_feature?.review || aiUsage || zeroUsage;
  const translationUsage = aiUsage?.by_feature?.translation || zeroUsage;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Bosh sahifa</h2>
          <p className="mt-1 text-sm text-slate-500">
            {currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative" ref={notificationRef}>
            <button
              type="button"
              onClick={() => setNotificationOpen((prev) => !prev)}
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
              title="Habarnomalar"
              aria-label="Habarnomalar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7C18 5.072 14.928 2 11.25 2S4.5 5.072 4.5 9.05v.7a8.967 8.967 0 01-2.31 6.022 23.848 23.848 0 005.454 1.31m7.213 0a24.255 24.255 0 01-7.214 0m7.214 0a3 3 0 11-7.214 0" />
              </svg>
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] leading-[18px] font-bold text-center">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </button>

            {notificationOpen && (
              <div className="absolute right-0 mt-2 w-[360px] sm:w-[420px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-xl z-30">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Habarnomalar</p>
                    <p className="text-xs text-slate-500 mt-0.5">{unreadNotificationCount} ta o'qilmagan</p>
                  </div>
                  {unreadNotificationCount > 0 && (
                    <button
                      onClick={markAllNotificationsAsSeen}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      Barchasini o'qilgan qilish
                    </button>
                  )}
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((item) => {
                      const meta = notificationMeta[item.type] || notificationMeta.new_submission;
                      const unread = !seenNotificationIds.has(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleNotificationClick(item)}
                          className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50 ${unread ? 'bg-blue-50/40' : 'bg-white'}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${meta.dotClass}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-700">{meta.label}</p>
                              <p className="text-sm text-slate-900 mt-0.5 truncate">{item.submissionTitle}</p>
                              <p className="text-xs text-slate-500 mt-1 truncate">{item.authorName || "Muallif noma'lum"}</p>
                              <p className="text-[11px] text-slate-400 mt-1">{formatDateTime(item.happenedAt)}</p>
                            </div>
                            {unread && <span className="mt-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-6 py-10 text-center">
                      <p className="text-sm text-slate-500">Hozircha habarnoma yo'q</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {exporting ? '⏳ Yuklanmoqda...' : '📥 Eksport CSV'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {statCards.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {statCards.map((card) => (
            <div key={card.label} className={`${card.gradient} rounded-2xl p-4 text-white`}>
              <div className="flex items-center justify-between mb-3">
                <div className="opacity-80">{card.icon}</div>
              </div>
              <p className="text-2xl font-bold">{card.value}</p>
              <p className="text-xs opacity-80 mt-1">{card.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-700 text-sm">
          Statistika ma'lumotlari yuklanmadi. Iltimos, tarmoqni tekshiring yoki qayta urining.
        </div>
      )}

      {/* Per-Issue Bar Chart + Status Pie Chart */}
      {stats && perIssue.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Sonlar bo'yicha maqolalar</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={perIssue.slice(-10).map(i => ({ name: `№${i.number}`, Jami: i.total, Qabul: i.accepted || 0, Chop: i.published || 0 }))}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
                <Bar dataKey="Jami" fill="#152347" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Qabul" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Chop" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Status taqsimoti</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Kutilmoqda', value: stats.by_status?.submitted || 0 },
                    { name: 'Taqrizda', value: stats.by_status?.under_review || 0 },
                    { name: 'Qayta ishlash', value: stats.by_status?.revision_required || 0 },
                    { name: 'Qabul', value: stats.by_status?.accepted || 0 },
                    { name: 'Rad', value: stats.by_status?.rejected || 0 },
                    { name: 'Chop etilgan', value: stats.by_status?.published || 0 },
                  ].filter(d => d.value > 0)}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value"
                >
                  {['#f59e0b', '#3b82f6', '#f97316', '#10b981', '#ef4444', '#8b5cf6'].map((color, i) => (
                    <Cell key={i} fill={color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Per-Issue Statistics */}
      {perIssue.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Sonlar bo'yicha statistika</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Son</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Jami</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Kutilmoqda</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Taqrizda</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Qayta ishlash</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Qabul</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Rad</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Chop</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Holat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {perIssue.map((iss) => (
                  <tr key={iss.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">
                      {iss.title} <span className="text-slate-400 text-xs">(Vol.{iss.volume}, №{iss.number}, {iss.year})</span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-slate-900">{iss.total}</td>
                    <td className="px-4 py-3 text-center text-sm text-amber-600 font-medium">{iss.submitted || 0}</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-600 font-medium">{iss.under_review || 0}</td>
                    <td className="px-4 py-3 text-center text-sm text-orange-500 font-medium">{iss.revision_required || 0}</td>
                    <td className="px-4 py-3 text-center text-sm text-emerald-600 font-medium">{iss.accepted || 0}</td>
                    <td className="px-4 py-3 text-center text-sm text-red-600 font-medium">{iss.rejected || 0}</td>
                    <td className="px-4 py-3 text-center text-sm text-violet-600 font-medium">{iss.published || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${iss.is_open ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${iss.is_open ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {iss.is_open ? 'Ochiq' : "Yopiq"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Affiliation & Country Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* By Affiliation */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Muassasa bo'yicha izlanuvchilar</h3>
          </div>
          <div className="overflow-y-auto max-h-80">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Muassasa</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Soni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {byAffiliation.length > 0 ? byAffiliation.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-slate-700">{item.affiliation}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700">{item.count}</span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={2} className="px-4 py-8 text-center text-slate-400 text-sm">Ma'lumot yo'q</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* By Country */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Davlat bo'yicha izlanuvchilar</h3>
          </div>
          <div className="overflow-y-auto max-h-80">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Davlat</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Soni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {byCountry.length > 0 ? byCountry.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-slate-700">{countryLabels[item.country] || item.country}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700">{item.count}</span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={2} className="px-4 py-8 text-center text-slate-400 text-sm">Ma'lumot yo'q</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* By Section × Issue Cross-Table */}
      {bySection.length > 0 && sectionByIssue.issues.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Yo'nalishlar bo'yicha statistika</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: Math.max(600, 180 + sectionByIssue.issues.length * 90) }}>
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/80 sticky left-0 z-10 min-w-[180px]">Yo'nalish</th>
                  {sectionByIssue.issues.map((iss) => (
                    <th key={iss.id} className="px-3 py-3.5 text-center text-xs font-semibold text-slate-500 whitespace-nowrap min-w-[80px]">
                      {iss.label}
                    </th>
                  ))}
                  <th className="px-4 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider bg-slate-50/50">Jami</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {bySection.map((sec, idx) => {
                  const sectionData = sectionByIssue.data[sec.section] || {};
                  return (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-slate-800 bg-slate-50/30 sticky left-0 z-10">
                        <span className="truncate block max-w-[200px]">{sec.section || 'Belgilanmagan'}</span>
                      </td>
                      {sectionByIssue.issues.map((iss) => {
                        const val = sectionData[String(iss.id)] || 0;
                        return (
                          <td key={iss.id} className="px-3 py-3 text-center">
                            {val > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-md text-xs font-bold bg-indigo-50 text-indigo-700">{val}</span>
                            ) : (
                              <span className="text-slate-200 text-xs">&mdash;</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center bg-slate-50/30">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white">{sec.count}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Token Usage Widget */}
      {aiUsage && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-8">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>
            </div>
            <h3 className="text-sm font-semibold text-slate-900">Sun'iy intellekt API foydalanish</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                <p className="text-xs text-violet-600 font-medium uppercase tracking-wider">Jami so'rovlar</p>
                <p className="text-2xl font-bold text-violet-900 mt-1">{aiUsage.total_requests}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Kirish tokenlari</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{(aiUsage.total_input_tokens || 0).toLocaleString()}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <p className="text-xs text-emerald-600 font-medium uppercase tracking-wider">Chiqish tokenlari</p>
                <p className="text-2xl font-bold text-emerald-900 mt-1">{(aiUsage.total_output_tokens || 0).toLocaleString()}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Umumiy narx (USD)</p>
                <p className="text-2xl font-bold text-amber-900 mt-1">${(aiUsage.total_cost_usd || 0).toFixed(4)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-blue-900">Tarjima (Claude Haiku)</h4>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                    {translationUsage.total_requests || 0} so'rov
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-xl border border-blue-100 p-3">
                    <p className="text-[11px] text-blue-500 uppercase tracking-wider font-medium">Kirish</p>
                    <p className="text-lg font-bold text-blue-900 mt-1">{(translationUsage.total_input_tokens || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-blue-100 p-3">
                    <p className="text-[11px] text-blue-500 uppercase tracking-wider font-medium">Chiqish</p>
                    <p className="text-lg font-bold text-blue-900 mt-1">{(translationUsage.total_output_tokens || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-blue-100 p-3 mb-3">
                  <p className="text-[11px] text-blue-500 uppercase tracking-wider font-medium">Narx (USD)</p>
                  <p className="text-lg font-bold text-blue-900 mt-1">${(translationUsage.total_cost_usd || 0).toFixed(4)}</p>
                </div>
                {(translationUsage.recent || []).length > 0 && (
                  <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                    {(translationUsage.recent || []).slice(0, 6).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white rounded-lg border border-blue-100 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">{item.kind || 'translation'}</p>
                          <p className="text-[11px] text-slate-500 truncate">{item.model}</p>
                        </div>
                        <p className="text-[11px] text-slate-600 ml-3 shrink-0">
                          {(item.input_tokens || 0).toLocaleString()} + {(item.output_tokens || 0).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-violet-100 bg-violet-50/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-violet-900">Taqriz (Claude Opus)</h4>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-violet-100 text-violet-700 font-medium">
                    {reviewUsage.total_requests || 0} so'rov
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-xl border border-violet-100 p-3">
                    <p className="text-[11px] text-violet-500 uppercase tracking-wider font-medium">Kirish</p>
                    <p className="text-lg font-bold text-violet-900 mt-1">{(reviewUsage.total_input_tokens || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-violet-100 p-3">
                    <p className="text-[11px] text-violet-500 uppercase tracking-wider font-medium">Chiqish</p>
                    <p className="text-lg font-bold text-violet-900 mt-1">{(reviewUsage.total_output_tokens || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-violet-100 p-3 mb-3">
                  <p className="text-[11px] text-violet-500 uppercase tracking-wider font-medium">Narx (USD)</p>
                  <p className="text-lg font-bold text-violet-900 mt-1">${(reviewUsage.total_cost_usd || 0).toFixed(4)}</p>
                </div>
                {(reviewUsage.recent || []).length > 0 && (
                  <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                    {(reviewUsage.recent || []).slice(0, 6).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white rounded-lg border border-violet-100 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">{item.title || 'Nomsiz maqola'}</p>
                          <p className="text-[11px] text-slate-500 truncate">{item.model}</p>
                        </div>
                        <p className="text-[11px] text-slate-600 ml-3 shrink-0">
                          {(item.input_tokens || 0).toLocaleString()} + {(item.output_tokens || 0).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Submissions */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Oxirgi maqolalar</h3>
          <Link to="/submissions" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            Barchasini ko'rish →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Sarlavha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Muallif</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Sana</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {recent.map((sub) => (
                <tr key={sub.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-3.5 text-sm text-slate-400">#{sub.id}</td>
                  <td className="px-6 py-3.5">
                    <Link to={`/submissions/${sub.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-700">
                      <span className="inline-flex items-center gap-1.5">
                        {!sub.is_read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 animate-pulse" title="O'qilmagan" />
                        )}
                        {sub.title.length > 50 ? sub.title.slice(0, 50) + '...' : sub.title}
                      </span>
                    </Link>
                  </td>
                  <td className="px-6 py-3.5 text-sm text-slate-600">
                    {sub.author ? `${sub.author.last_name} ${sub.author.first_name}` : '-'}
                  </td>
                  <td className="px-6 py-3.5"><StatusBadge status={sub.status} /></td>
                  <td className="px-6 py-3.5 text-sm text-slate-400">{formatDate(sub.submitted_at)}</td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400 text-sm">
                    Hali maqolalar yo'q
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
